import { BaseModule } from "./baseModule";
import type {
  Profile,
  User,
  Resume,
  ProfileResponse,
  UpdateProfileRequest,
  Experience,
  CreateExperienceRequest,
  UpdateExperienceRequest,
  Education,
  CreateEducationRequest,
  UpdateEducationRequest,
} from "./types";

import { API_ENDPOINTS, isValidUrl, isValidDateString } from "./types";

/**
 * ProfileModule handles all profile-related operations including
 * core profile management, experience CRUD, and education CRUD
 */
export class ProfileModule extends BaseModule {
  // ============================================================================
  // Core Profile Operations
  // ============================================================================

  /**
   * Get the current user's profile
   * @returns Promise<ProfileResponse> - Profile data with user information
   */
  async getProfile(): Promise<ProfileResponse> {
    const response = await this.get<{
      user: User;
      is_admin: boolean;
      resume_status: string;
      profile: Profile | null;
      latest_resume: Resume | null;
    }>(API_ENDPOINTS.PROFILE);

    // Transform the response to match our expected ProfileResponse format
    const profile: Profile = response.profile || {
      userId: response.user.id,
      firstName: response.user.name?.split(" ")[0] || "",
      lastName: response.user.name?.split(" ").slice(1).join(" ") || "",
      skills: [],
      resumeStatus: response.resume_status,
    };

    return {
      profile,
      user: response.user,
    };
  }

  /**
   * Update the current user's profile
   * @param data - Profile update data
   * @returns Promise<Profile> - Updated profile
   */
  async updateProfile(data: UpdateProfileRequest): Promise<Profile> {
    // Validate profile data before sending
    this.validateProfileData(data);

    // Transform the data to match backend expectations
    const transformedData = this.transformRequest(data);

    return this.put<Profile>(API_ENDPOINTS.PROFILE, transformedData);
  }

  // ============================================================================
  // Experience Management
  // ============================================================================

  /**
   * Get all experiences for the current user
   * @returns Promise<Experience[]> - List of user experiences
   */
  async getExperiences(): Promise<Experience[]> {
    return this.get<Experience[]>(API_ENDPOINTS.PROFILE_EXPERIENCE);
  }

  /**
   * Create a new experience record
   * @param data - Experience creation data
   * @returns Promise<Experience> - Created experience
   */
  async createExperience(data: CreateExperienceRequest): Promise<Experience> {
    // Validate experience data
    this.validateExperienceData(data);

    // Transform dates to proper format
    const transformedData = this.transformRequest(data);

    return this.post<Experience>(
      API_ENDPOINTS.PROFILE_EXPERIENCE,
      transformedData,
    );
  }

  /**
   * Update an existing experience record
   * @param id - Experience ID
   * @param data - Experience update data
   * @returns Promise<Experience> - Updated experience
   */
  async updateExperience(
    id: string,
    data: UpdateExperienceRequest,
  ): Promise<Experience> {
    if (!id) {
      throw new Error("Experience ID is required for update");
    }

    // Validate experience data
    this.validateExperienceData(data, true);

    // Transform dates to proper format
    const transformedData = this.transformRequest(data);

    return this.put<Experience>(
      `${API_ENDPOINTS.PROFILE_EXPERIENCE}/${id}`,
      transformedData,
    );
  }

  /**
   * Delete an experience record
   * @param id - Experience ID
   * @returns Promise<void>
   */
  async deleteExperience(id: string): Promise<void> {
    if (!id) {
      throw new Error("Experience ID is required for deletion");
    }

    await this.delete<void>(`${API_ENDPOINTS.PROFILE_EXPERIENCE}/${id}`);
  }

  // ============================================================================
  // Education Management
  // ============================================================================

  /**
   * Get all education records for the current user
   * @returns Promise<Education[]> - List of user education records
   */
  async getEducation(): Promise<Education[]> {
    return this.get<Education[]>(API_ENDPOINTS.PROFILE_EDUCATION);
  }

  /**
   * Create a new education record
   * @param data - Education creation data
   * @returns Promise<Education> - Created education record
   */
  async createEducation(data: CreateEducationRequest): Promise<Education> {
    // Validate education data
    this.validateEducationData(data);

    // Transform dates to proper format
    const transformedData = this.transformRequest(data);

    return this.post<Education>(
      API_ENDPOINTS.PROFILE_EDUCATION,
      transformedData,
    );
  }

  /**
   * Update an existing education record
   * @param id - Education ID
   * @param data - Education update data
   * @returns Promise<Education> - Updated education record
   */
  async updateEducation(
    id: string,
    data: UpdateEducationRequest,
  ): Promise<Education> {
    if (!id) {
      throw new Error("Education ID is required for update");
    }

    // Validate education data
    this.validateEducationData(data, true);

    // Transform dates to proper format
    const transformedData = this.transformRequest(data);

    return this.put<Education>(
      `${API_ENDPOINTS.PROFILE_EDUCATION}/${id}`,
      transformedData,
    );
  }

  /**
   * Delete an education record
   * @param id - Education ID
   * @returns Promise<void>
   */
  async deleteEducation(id: string): Promise<void> {
    if (!id) {
      throw new Error("Education ID is required for deletion");
    }

    await this.delete<void>(`${API_ENDPOINTS.PROFILE_EDUCATION}/${id}`);
  }

  // ============================================================================
  // Data Validation and Sanitization
  // ============================================================================

  /**
   * Validate profile data before sending to backend
   * @param data - Profile data to validate
   */
  private validateProfileData(data: UpdateProfileRequest): void {
    // Validate email format if provided
    if (data.website && !isValidUrl(data.website)) {
      throw new Error("Invalid website URL format");
    }

    if (data.linkedinUrl && !isValidUrl(data.linkedinUrl)) {
      throw new Error("Invalid LinkedIn URL format");
    }

    if (data.githubUrl && !isValidUrl(data.githubUrl)) {
      throw new Error("Invalid GitHub URL format");
    }

    // Validate phone number format (basic validation)
    if (
      data.phone &&
      !/^\+?[1-9]\d{0,15}$/.test(data.phone.replace(/[\s()-]/g, ""))
    ) {
      throw new Error("Invalid phone number format");
    }

    // Validate skills array
    if (data.skills) {
      if (!Array.isArray(data.skills)) {
        throw new Error("Skills must be an array");
      }

      if (
        data.skills.some(
          (skill) => typeof skill !== "string" || skill.trim().length === 0,
        )
      ) {
        throw new Error("All skills must be non-empty strings");
      }

      if (data.skills.length > 50) {
        throw new Error("Maximum 50 skills allowed");
      }
    }

    // Sanitize string fields
    if (data.firstName) data.firstName = data.firstName.trim();
    if (data.lastName) data.lastName = data.lastName.trim();
    if (data.location) data.location = data.location.trim();
    if (data.bio) data.bio = data.bio.trim();
    if (data.phone) data.phone = data.phone.trim();
  }

  /**
   * Validate experience data before sending to backend
   * @param data - Experience data to validate
   * @param isUpdate - Whether this is an update operation
   */
  private validateExperienceData(
    data: CreateExperienceRequest | UpdateExperienceRequest,
    isUpdate: boolean = false,
  ): void {
    // Required fields for creation
    if (!isUpdate) {
      const createData = data as CreateExperienceRequest;
      if (!createData.company?.trim()) {
        throw new Error("Company name is required");
      }
      if (!createData.title?.trim()) {
        throw new Error("Job title is required");
      }
      if (!createData.start_date) {
        throw new Error("Start date is required");
      }
    }

    // Validate dates
    if (data.start_date && !isValidDateString(data.start_date)) {
      throw new Error("Invalid start date format");
    }

    if (data.end_date && !isValidDateString(data.end_date)) {
      throw new Error("Invalid end date format");
    }

    // Validate date logic
    if (data.start_date && data.end_date) {
      const startDate = new Date(data.start_date);
      const endDate = new Date(data.end_date);

      if (endDate <= startDate) {
        throw new Error("End date must be after start date");
      }
    }

    // Sanitize string fields
    if (data.company) data.company = data.company.trim();
    if (data.title) data.title = data.title.trim();
    if (data.description) data.description = data.description.trim();
  }

  /**
   * Validate education data before sending to backend
   * @param data - Education data to validate
   * @param isUpdate - Whether this is an update operation
   */
  private validateEducationData(
    data: CreateEducationRequest | UpdateEducationRequest,
    isUpdate: boolean = false,
  ): void {
    // Required fields for creation
    if (!isUpdate) {
      const createData = data as CreateEducationRequest;
      if (!createData.institution?.trim()) {
        throw new Error("Institution name is required");
      }
      if (!createData.degree?.trim()) {
        throw new Error("Degree is required");
      }
      if (!createData.start_date) {
        throw new Error("Start date is required");
      }
    }

    // Validate dates
    if (data.start_date && !isValidDateString(data.start_date)) {
      throw new Error("Invalid start date format");
    }

    if (data.end_date && !isValidDateString(data.end_date)) {
      throw new Error("Invalid end date format");
    }

    // Validate date logic
    if (data.start_date && data.end_date) {
      const startDate = new Date(data.start_date);
      const endDate = new Date(data.end_date);

      if (endDate <= startDate) {
        throw new Error("End date must be after start date");
      }
    }

    // Sanitize string fields
    if (data.institution) data.institution = data.institution.trim();
    if (data.degree) data.degree = data.degree.trim();
    if (data.field_of_study) data.field_of_study = data.field_of_study.trim();
    if (data.description) data.description = data.description.trim();
  }

  // ============================================================================
  // Skills Management Helper Methods
  // ============================================================================

  /**
   * Add a skill to the user's profile
   * @param skill - Skill to add
   * @returns Promise<Profile> - Updated profile
   */
  async addSkill(skill: string): Promise<Profile> {
    if (!skill?.trim()) {
      throw new Error("Skill cannot be empty");
    }

    const currentProfile = await this.getProfile();
    const currentSkills = currentProfile.profile.skills || [];

    const normalizedSkill = skill.trim();

    // Check if skill already exists (case-insensitive)
    if (
      currentSkills.some(
        (s) => s.toLowerCase() === normalizedSkill.toLowerCase(),
      )
    ) {
      throw new Error("Skill already exists");
    }

    const updatedSkills = [...currentSkills, normalizedSkill];

    return this.updateProfile({ skills: updatedSkills });
  }

  /**
   * Remove a skill from the user's profile
   * @param skill - Skill to remove
   * @returns Promise<Profile> - Updated profile
   */
  async removeSkill(skill: string): Promise<Profile> {
    if (!skill?.trim()) {
      throw new Error("Skill cannot be empty");
    }

    const currentProfile = await this.getProfile();
    const currentSkills = currentProfile.profile.skills || [];

    const normalizedSkill = skill.trim();

    const updatedSkills = currentSkills.filter(
      (s) => s.toLowerCase() !== normalizedSkill.toLowerCase(),
    );

    return this.updateProfile({ skills: updatedSkills });
  }

  /**
   * Update the entire skills array
   * @param skills - New skills array
   * @returns Promise<Profile> - Updated profile
   */
  async updateSkills(skills: string[]): Promise<Profile> {
    if (!Array.isArray(skills)) {
      throw new Error("Skills must be an array");
    }

    // Validate and sanitize skills
    const sanitizedSkills = skills
      .map((skill) => skill?.trim())
      .filter((skill) => skill && skill.length > 0)
      .filter(
        (skill, index, arr) =>
          // Remove duplicates (case-insensitive)
          arr.findIndex((s) => s.toLowerCase() === skill.toLowerCase()) ===
          index,
      );

    if (sanitizedSkills.length > 50) {
      throw new Error("Maximum 50 skills allowed");
    }

    return this.updateProfile({ skills: sanitizedSkills });
  }

  // ============================================================================
  // Profile Status Tracking
  // ============================================================================

  /**
   * Get profile completion status
   * @returns Promise<{isComplete: boolean, missingFields: string[], completionPercentage: number}>
   */
  async getProfileCompletionStatus(): Promise<{
    isComplete: boolean;
    missingFields: string[];
    completionPercentage: number;
  }> {
    const profileResponse = await this.getProfile();
    const profile = profileResponse.profile;

    const requiredFields = [
      "firstName",
      "lastName",
      "location",
      "bio",
      "skills",
    ];

    const optionalFields = ["phone", "website", "linkedinUrl", "githubUrl"];

    const missingFields: string[] = [];
    let filledRequiredFields = 0;
    let filledOptionalFields = 0;

    // Check required fields
    requiredFields.forEach((field) => {
      const value = profile[field as keyof Profile];
      if (!value || (Array.isArray(value) && value.length === 0)) {
        missingFields.push(field);
      } else {
        filledRequiredFields++;
      }
    });

    // Check optional fields
    optionalFields.forEach((field) => {
      const value = profile[field as keyof Profile];
      if (value && (!Array.isArray(value) || value.length > 0)) {
        filledOptionalFields++;
      }
    });

    const totalFields = requiredFields.length + optionalFields.length;
    const filledFields = filledRequiredFields + filledOptionalFields;
    const completionPercentage = Math.round((filledFields / totalFields) * 100);

    return {
      isComplete: missingFields.length === 0,
      missingFields,
      completionPercentage,
    };
  }
}
